/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useId, useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Center,
    Tooltip,
    Flex,
    useMultiStyleConfig,
    omitThemingProps,
    useRadio,
    Text
} from '@chakra-ui/react'
import useBreakpoints from '../../hooks/use-breakpoints'

/**
 * The Swatch Component displays item inside `SwatchGroup`
 */
const Swatch = (props) => {
    const styles = useMultiStyleConfig('SwatchGroup', props)
    const {
        disabled,
        selected,
        orderable,
        htmlName,
        label,
        children,
        href,
        onChange,
        value,
        name,
        propagateDisabled,
        tooltipPlacement,
        onHoverChange,
        visuallyHiddenName,
        containerHtmlProps,
        type,
        ...ownProps
    } = omitThemingProps(props)
    const hideTooltip = useBreakpoints({base: true, lg: false})
    const labelId = useId()
    const {getInputProps, getLabelProps, htmlProps, getRadioProps} = useRadio({
        id: `${htmlName}-${value}`,
        name: htmlName,
        value: value,
        'aria-label': name,
        isChecked: selected,
        isDisabled: propagateDisabled && disabled,
        onChange: (e) => {
            //this is needed to open the quickshopmodal when clicking on the +x color-swatch
            if (!!value && !!href) {
                e.stopPropagation()
                onChange(value, href)
                onHoverChange?.()
            }
        }
    })

    const inputProps = useMemo(() => {
        const inputProps = getInputProps()
        if (type) {
            inputProps.type = type
        }
        return {
            ...inputProps,
            style: {
                ...inputProps.style,
                // center the radio input so it doesn't cause scroll of parent container when partially visible and selected
                left: '50%'
            }
        }
    }, [getInputProps, type])

    return (
        <Flex
            as="label"
            alignItems="flex-start"
            cursor="pointer"
            {...htmlProps}
            {...containerHtmlProps}
        >
            <input {...inputProps} aria-labelledby={labelId} />
            <Tooltip
                hasArrow
                label={name}
                isDisabled={hideTooltip}
                {...(tooltipPlacement && {placement: tooltipPlacement})}
            >
                <Box
                    as="div"
                    {...getRadioProps()}
                    {...styles.swatch}
                    data-testid="swatch"
                    {...ownProps}
                >
                    <Center {...styles.swatchButton}>
                        {children}
                        {label && <Box {...styles.label}>{label}</Box>}
                    </Center>
                </Box>
            </Tooltip>
            <Text className="sr-only" id={labelId} {...getLabelProps()}>
                {visuallyHiddenName || name}
            </Text>
        </Flex>
    )
}

Swatch.displayName = 'Swatch'

Swatch.propTypes = {
    /**
     * Size of the swatches
     */
    size: PropTypes.string,
    /**
     * The children to be rendered within swatch item.
     */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    /**
     * Additional HTML attributes to be applied to the swatch container element.
     */
    containerHtmlProps: PropTypes.object,
    /**
     * Indicates whether the option is disabled
     */
    disabled: PropTypes.bool,
    /**
     * Indicates whether the option is orderable
     */
    orderable: PropTypes.bool,
    /**
     * Indicates whether the option is selected.
     * This props is provided internally by SwatchGroup
     */
    selected: PropTypes.bool,
    /**
     * The shape of the Swatch
     */
    variant: PropTypes.oneOf(['square', 'circle', 'chip', 'thumbnails']),
    /**
     * The label of the option.
     */
    label: PropTypes.string,
    /**
     *  The url of this option
     */
    href: PropTypes.string,
    /**
     * This function is called whenever the user selects an option.
     * It is passed the new value.
     */
    onChange: PropTypes.func,
    /**
     * The value for the option.
     */
    value: PropTypes.string,
    /**
     * The display value for each swatch
     */
    name: PropTypes.string,
    /**
     * Indicates whether the disabled attribute should be
     * propagated to the inactive color swatches
     */
    propagateDisabled: PropTypes.bool,
    /**
     * Position of the tooltip info to display for swatch
     */
    tooltipPlacement: PropTypes.string,
    /**
     * This function is called when cursor is over or move out of a color value
     */
    onHoverChange: PropTypes.func,
    /**
     * The display screen reader value for each swatch
     */
    visuallyHiddenName: PropTypes.node,
    /**
     * Optional input type override for the swatch.
     * Defaults to `radio`. Can be set to `"button"` or any valid HTML input type.
     */
    type: PropTypes.string
}

export default Swatch
